import {
  Button,
  CollapseWithLabel,
  Datepicker,
  TextInput,
  Toggler,
} from '@components';
import {
  TempoContribuicaoDocs,
  TempoContribuicaoInitialValues,
  TempoContribuicaoSchema,
} from '@schemas';
import {
  adicionaItemRascunhoRequest,
  atualizaItemRascunhoRequest,
  concluirFormRequest,
  criaFormRascunhoRequest,
  excluiItemRascunhoRequest,
} from '@store/user/actions';
import { Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ModalUpload } from './components/ModalUpload';
import { validaUpload } from './utils/validaUpload';

const CardEntidade = ({
  nome,
  inicio,
  termino,
  tempoEspecial,
  anexos,
  setDisabled,
}) => {
  const formattedInitialAnexos = anexos.reduce(
    (previousValue, currentValue) => {
      return { ...previousValue, [currentValue.name]: currentValue };
    },
    {}
  );

  const initial = {
    nome,
    inicio,
    termino,
    tempoEspecial,
    ...formattedInitialAnexos,
  };

  return (
    <>
      <hr className="mt-32 mt-xs-0 mb-32" />
      <CollapseWithLabel
        label={
          <>
            <strong>Entidade: </strong>
            {nome}
          </>
        }
      >
        <FormikBody type="update" initial={initial} setDisabled={setDisabled} />
      </CollapseWithLabel>
    </>
  );
};

const Anexos = ({ disabled, reset, id }) => {
  const empresas = useSelector((state) => state.user.formularioLista);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  return (
    <>
      <ModalUpload modalIsOpen={modalIsOpen} setModalIsOpen={setModalIsOpen} />
      {TempoContribuicaoDocs.map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
            >
              {({ form }) => {
                const itemAtual = empresas.find((dp) => dp.nome === id);
                const anexoAtual = itemAtual?.anexos.find(
                  (anx) => anx.name === anexo.name
                );

                return (
                  <TextInput
                    key={reset ? 'reset' : ''}
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return validaUpload({
                        anexo,
                        e,
                        form,
                        setModalIsOpen,
                      });
                    }}
                    disabled={disabled}
                    defaultFile={anexoAtual}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}
    </>
  );
};

const FormikBody = ({ initial, type, setDisabled }) => {
  const dispatch = useDispatch();
  const isUpdate = type === 'update';
  const [isEditing, setIsEditing] = useState(!isUpdate);
  const [calledReset, setCalledReset] = useState(false);

  useEffect(() => {
    setDisabled(isEditing);
  }, [isEditing, setDisabled]);

  return (
    <>
      <hr className="mt-32 mt-xs-0 mb-32" />
      <Formik
        validateOnMount
        initialValues={initial || TempoContribuicaoInitialValues}
        validationSchema={TempoContribuicaoSchema}
        onSubmit={(values, { resetForm }) => {
          setDisabled(false);

          const successMessage = 'Entidade adicionada com sucesso';

          if (isUpdate) {
            const inicio =
              values?.inicio?.toLocaleDateString('pt') || values.inicio;
            const termino =
              values?.termino?.toLocaleDateString('pt') || values.termino;

            dispatch(
              atualizaItemRascunhoRequest({
                campos: {
                  ...values,
                  inicio,
                  termino,
                },
                camposAntigos: initial,
                successMessage: successMessage.replace(
                  'adicionada',
                  'atualizada'
                ),
              })
            );

            setIsEditing(false);
          } else {
            dispatch(
              adicionaItemRascunhoRequest({
                campos: {
                  ...values,
                  inicio: values.inicio.toLocaleDateString('pt'),
                  termino: values.termino.toLocaleDateString('pt'),
                },
                successMessage,
              })
            );

            resetForm();
            setCalledReset(true);
            setTimeout(() => {
              setCalledReset(false);
            }, 150);
          }
        }}
      >
        {({ handleSubmit, isValid, values }) => (
          <Form>
            <div className="row">
              <div className="col-md-12 col-lg-6">
                <Field
                  className="mb-3"
                  name="nome"
                  component={TextInput}
                  label="Nome Empresa/Entidade/Empregado/Autônomo"
                  disabled={!isEditing}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 col-lg-6 mb-lg-32">
                <Field
                  name="inicio"
                  component={Datepicker}
                  datePickerClassName="formSize"
                  max={new Date()}
                  className="mb-3"
                  label="Início"
                  disabled={!isEditing}
                  showYearDropdown
                  yearDropdownItemNumber={100}
                  scrollableYearDropdown
                />
              </div>
              <div className="col-md-12 col-lg-6 mb-lg-32">
                <Field
                  name="termino"
                  component={Datepicker}
                  datePickerClassName="formSize"
                  max={new Date()}
                  className="mb-3"
                  label="Término"
                  disabled={!isEditing}
                  showYearDropdown
                  yearDropdownItemNumber={100}
                  scrollableYearDropdown
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 col-lg-6">
                <Field
                  className="mb-3"
                  name="tempoEspecial"
                  component={Toggler}
                  label="Possibilidade Averbação (Tempo especial)"
                  disabled={!isEditing}
                />
              </div>
            </div>
            <Anexos
              disabled={!isEditing}
              reset={calledReset}
              id={values.nome}
            />
            {isEditing ? (
              <>
                <hr className="mt-32 mt-xs-0 mb-32" />
                <div className="row">
                  <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                    <div className="d-block d-md-flex align-items-center">
                      <Button
                        disabled={!isValid}
                        className="ms-0 ms-md-3"
                        onClick={handleSubmit}
                      >
                        {isUpdate ? 'Atualizar' : 'Adicionar'}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <hr className="mt-32 mt-xs-0 mb-32" />
                <div className="row">
                  <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                    <div className="d-block d-md-flex align-items-center">
                      <Button
                        className="ms-0 ms-md-3"
                        onClick={() => {
                          dispatch(excluiItemRascunhoRequest(values));
                        }}
                      >
                        Excluir
                      </Button>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                    <div className="d-block d-md-flex align-items-center">
                      <Button
                        className="ms-0 ms-md-3"
                        onClick={() => setIsEditing(true)}
                      >
                        Editar
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export const TempoContribuicao = ({ title, reference }) => {
  const [disabled, setDisabled] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const entidades = useSelector((state) => state.user.formularioLista);

  useEffect(() => {
    dispatch(criaFormRascunhoRequest(reference));
  }, [dispatch, reference]);

  return (
    <div className="card">
      <div className="card-body">
        <h3 className="fw-bold ts-20 mb-32">{title}</h3>
        <FormikBody setDisabled={setDisabled} />
        {entidades.map((entidade) => {
          return (
            <CardEntidade
              key={entidade.nome}
              {...entidade}
              setDisabled={setDisabled}
            />
          );
        })}
        {entidades.length > 0 && (
          <>
            <hr className="mt-32 mt-xs-0 mb-32" />
            <div className="row">
              <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                <div className="d-block d-md-flex align-items-center">
                  <Button
                    disabled={disabled}
                    className="ms-0 ms-md-3"
                    onClick={() => {
                      dispatch(concluirFormRequest(reference));
                      navigate('/requerimentos/novo');
                    }}
                  >
                    Enviar
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
